// apps/web/src/components/campaigns/CampaignAnalytics.tsx
// Campaign analytics dashboard

import { useState, useEffect } from 'react';

interface CampaignAnalyticsData {
  campaignId: string;
  impressions: number;
  clicks: number;
  conversions: number;
  roi: number;
  engagementRate: number;
  avgEngagement: number;
  audienceGrowth: number;
  contentPerformance: any[];
  channelPerformance: any[];
}

export function CampaignAnalytics({ campaignId }: { campaignId: string }) {
  const [analytics, setAnalytics] = useState<CampaignAnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        setLoading(true);
        // This would call the actual analytics API
        // const response = await apiClient.get(`/api/v1/analytics/campaigns/${campaignId}`);
        // For demo purposes, we'll simulate data
        setTimeout(() => {
          setAnalytics({
            campaignId,
            impressions: 125000,
            clicks: 8500,
            conversions: 1200,
            roi: 3.2,
            engagementRate: 4.5,
            avgEngagement: 2.1,
            audienceGrowth: 15.3,
            contentPerformance: [
              { contentId: '1', title: 'Video Promo', impressions: 45000, clicks: 3200, conversions: 450 },
              { contentId: '2', title: 'Blog Post', impressions: 32000, clicks: 2100, conversions: 320 },
              { contentId: '3', title: 'Social Post', impressions: 48000, clicks: 3200, conversions: 430 }
            ],
            channelPerformance: [
              { channel: 'Instagram', impressions: 52000, clicks: 3100, conversions: 480 },
              { channel: 'YouTube', impressions: 45000, clicks: 2800, conversions: 350 },
              { channel: 'TikTok', impressions: 28000, clicks: 2600, conversions: 370 }
            ]
          });
          setLoading(false);
        }, 1000);
      } catch (err) {
        setError('Failed to load analytics data');
        console.error('Error loading analytics:', err);
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, [campaignId]);

  if (loading) {
    return <div className="loading">Loading analytics...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  if (!analytics) {
    return <div className="error">No analytics data available</div>;
  }

  return (
    <div className="campaign-analytics">
      <h2>Campaign Analytics</h2>
      
      <div className="analytics-summary">
        <div className="metric-card">
          <h3>{analytics.impressions.toLocaleString()}</h3>
          <p>Impressions</p>
        </div>
        <div className="metric-card">
          <h3>{analytics.clicks.toLocaleString()}</h3>
          <p>Clicks</p>
        </div>
        <div className="metric-card">
          <h3>{analytics.conversions.toLocaleString()}</h3>
          <p>Conversions</p>
        </div>
        <div className="metric-card">
          <h3>{analytics.roi.toFixed(1)}x</h3>
          <p>ROI</p>
        </div>
        <div className="metric-card">
          <h3>{analytics.engagementRate}%</h3>
          <p>Engagement Rate</p>
        </div>
      </div>

      <div className="analytics-charts">
        <div className="chart-section">
          <h3>Content Performance</h3>
          <div className="content-performance">
            {analytics.contentPerformance.map((item, index) => (
              <div key={index} className="content-item">
                <h4>{item.title}</h4>
                <p>Impressions: {item.impressions.toLocaleString()}</p>
                <p>Clicks: {item.clicks.toLocaleString()}</p>
                <p>Conversions: {item.conversions.toLocaleString()}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="chart-section">
          <h3>Channel Performance</h3>
          <div className="channel-performance">
            {analytics.channelPerformance.map((item, index) => (
              <div key={index} className="channel-item">
                <h4>{item.channel}</h4>
                <p>Impressions: {item.impressions.toLocaleString()}</p>
                <p>Clicks: {item.clicks.toLocaleString()}</p>
                <p>Conversions: {item.conversions.toLocaleString()}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}